import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

// Dedicated no-auth client so Realtime connects instantly without waiting
// for session restore or token refresh that the shared client does.
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } }
);

const DISPLAY_CHANNEL = 'trc-pos-display';
const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const EMPTY = { status: 'idle', items: [], subtotal: 0, discount: 0, total: 0, customer: null, phone: null, promoCode: null, promoDiscount: 0, addlDiscount: 0, addlDiscLabel: null, txnRef: null, payMethod: null, upiQrUrl: null };

export default function CustomerDisplay() {
  const [s, setS]                = useState(EMPTY);
  const [connected, setConnected] = useState(false);
  const idleTimer                 = useRef(null);
  const listRef                  = useRef(null);

  useEffect(() => {
    const chan = supabase.channel(DISPLAY_CHANNEL, { config: { broadcast: { self: false } } });
    chan.on('broadcast', { event: 'state' }, ({ payload }) => {
      setS({ ...EMPTY, ...payload });
      clearTimeout(idleTimer.current);
      if (payload?.status === 'paid') {
        idleTimer.current = setTimeout(() => setS(EMPTY), 14000);
      }
    });
    chan.subscribe((status) => {
      const ok = status === 'SUBSCRIBED';
      setConnected(ok);
      if (ok) chan.send({ type: 'broadcast', event: 'request', payload: {} }).catch(() => {});
    });
    return () => { clearTimeout(idleTimer.current); supabase.removeChannel(chan); };
  }, []);

  // Scroll to bottom whenever items change so latest item is always visible
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [s.items?.length]);

  // ── IDLE ──────────────────────────────────────────────────────────────────
  if (s.status === 'idle' || (!s.items?.length && s.status !== 'paid')) {
    return (
      <div style={shell}>
        <div style={col}>
          <Header connected={connected} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 'clamp(64px,12vw,120px)' }}>☕</div>
            <div style={{ fontSize: 'clamp(28px,5vw,60px)', fontWeight: 900, color: '#0f172a' }}>Welcome!</div>
            <div style={{ fontSize: 'clamp(14px,1.8vw,22px)', color: '#64748b' }}>
              Happy reading at Tapas Reading Cafe
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PAID ──────────────────────────────────────────────────────────────────
  if (s.status === 'paid') {
    return (
      <div style={shell}>
        <div style={{ ...col, background: '#f0fdf4' }}>
          <Header connected={connected} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 'clamp(10px,2vw,22px)', textAlign: 'center' }}>
            <div style={{ fontSize: 'clamp(64px,12vw,120px)' }}>✅</div>
            <div style={{ fontSize: 'clamp(24px,4vw,48px)', fontWeight: 900, color: '#0f172a' }}>
              Payment Received!
            </div>
            <div style={{ fontSize: 'clamp(40px,9vw,96px)', fontWeight: 900, color: '#16a34a', letterSpacing: -2 }}>
              {fmt(s.total)}
            </div>
            {s.txnRef && (
              <div style={{ fontSize: 'clamp(12px,1.4vw,17px)', color: '#64748b' }}>Ref: {s.txnRef}</div>
            )}
            <div style={{ fontSize: 'clamp(14px,1.8vw,22px)', color: '#64748b', marginTop: 4 }}>
              Thank you for visiting TRC!
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── ACTIVE CART (receipt layout) ──────────────────────────────────────────
  const itemCount = s.items.reduce((t, i) => t + i.qty, 0);

  return (
    <div style={shell}>
      <div style={col}>
      <Header connected={connected} />

      {/* Customer + phone */}
      {s.customer && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'clamp(10px,1.4vw,16px) clamp(24px,3.5vw,52px)',
          background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>👤</span>
            <span style={{ fontSize: 'clamp(14px,1.8vw,22px)', fontWeight: 700, color: '#1e293b' }}>
              {s.customer}
            </span>
          </div>
          {s.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7,
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: 8, padding: 'clamp(5px,.8vw,9px) clamp(10px,1.4vw,18px)' }}>
              <span>📱</span>
              <span style={{ fontSize: 'clamp(14px,1.8vw,22px)', fontWeight: 700, color: '#1d4ed8' }}>
                {s.phone}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Column headers */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        padding: 'clamp(6px,1vw,12px) clamp(24px,3.5vw,52px)',
        background: '#f1f5f9', borderBottom: '2px solid #e2e8f0',
        fontSize: 'clamp(10px,1.1vw,13px)', fontWeight: 700,
        color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase', flexShrink: 0 }}>
        <span>Item <span style={{ fontWeight: 500, opacity: 0.75 }}>· {itemCount} item{itemCount !== 1 ? 's' : ''}</span></span>
        <span>Amount</span>
      </div>

      {/* Items list — scrollable */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto' }}>
        {s.items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: 'clamp(12px,1.6vw,20px) clamp(24px,3.5vw,52px)',
            borderBottom: '1px solid #f1f5f9',
            background: i % 2 === 0 ? '#ffffff' : '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              {it.qty > 1 && (
                <span style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
                  borderRadius: 6, padding: '2px 10px',
                  fontSize: 'clamp(11px,1.2vw,15px)', fontWeight: 700, flexShrink: 0 }}>
                  ×{it.qty}
                </span>
              )}
              <span style={{ fontSize: 'clamp(16px,2vw,26px)', fontWeight: 500, color: '#1e293b',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.name}
              </span>
              {it.discLabel && (
                <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
                  borderRadius: 6, padding: '2px 10px',
                  fontSize: 'clamp(11px,1.2vw,15px)', fontWeight: 700, flexShrink: 0 }}>
                  {it.discLabel} off
                </span>
              )}
            </div>
            <span style={{ fontSize: 'clamp(16px,2vw,26px)', fontWeight: 700,
              color: '#0f172a', marginLeft: 16, flexShrink: 0, textAlign: 'right' }}>
              {it.discLabel ? (
                <>
                  <span style={{ display: 'block', fontSize: 'clamp(11px,1.2vw,15px)', fontWeight: 600,
                    color: '#cbd5e1', textDecoration: 'line-through' }}>
                    {fmt(it.price * it.qty)}
                  </span>
                  <span style={{ color: '#059669' }}>{fmt(it.lineTotal)}</span>
                </>
              ) : fmt(it.lineTotal ?? it.price * it.qty)}
            </span>
          </div>
        ))}
      </div>

      {/* Bill summary footer */}
      <div style={{ flexShrink: 0, background: '#ffffff', borderTop: '2px solid #e2e8f0' }}>

        {/* Subtotal row */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          padding: 'clamp(8px,1vw,12px) clamp(24px,3.5vw,52px)',
          borderBottom: '1px solid #f1f5f9',
          fontSize: 'clamp(13px,1.6vw,19px)', color: '#64748b' }}>
          <span>Subtotal</span>
          <span>{fmt(s.subtotal)}</span>
        </div>

        {/* Promo code discount row */}
        {s.promoDiscount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: 'clamp(8px,1vw,12px) clamp(24px,3.5vw,52px)',
            borderBottom: '1px solid #f1f5f9',
            fontSize: 'clamp(13px,1.6vw,19px)', color: '#dc2626', fontWeight: 600 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              Promo
              {s.promoCode && (
                <span style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a',
                  borderRadius: 6, padding: '2px 10px', fontSize: 'clamp(11px,1.2vw,14px)',
                  fontWeight: 700, letterSpacing: '1px' }}>
                  {s.promoCode}
                </span>
              )}
            </span>
            <span>−{fmt(s.promoDiscount)}</span>
          </div>
        )}

        {/* Additional manual discount row */}
        {s.addlDiscount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: 'clamp(8px,1vw,12px) clamp(24px,3.5vw,52px)',
            borderBottom: '1px solid #f1f5f9',
            fontSize: 'clamp(13px,1.6vw,19px)', color: '#dc2626', fontWeight: 600 }}>
            <span>
              {s.promoCode ? 'Extra Discount' : 'Discount'}
              {s.promoCode && s.addlDiscLabel && (
                <span style={{ marginLeft: 8, fontSize: 'clamp(11px,1.2vw,14px)',
                  fontWeight: 700, opacity: 0.75 }}>({s.addlDiscLabel})</span>
              )}
            </span>
            <span>−{fmt(s.addlDiscount)}</span>
          </div>
        )}

        {/* Fallback: single discount row when no promo breakdown available */}
        {s.discount > 0 && !s.promoDiscount && !s.addlDiscount && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: 'clamp(8px,1vw,12px) clamp(24px,3.5vw,52px)',
            borderBottom: '1px solid #f1f5f9',
            fontSize: 'clamp(13px,1.6vw,19px)', color: '#dc2626', fontWeight: 600 }}>
            <span>Discount</span>
            <span>−{fmt(s.discount)}</span>
          </div>
        )}

        {/* UPI QR section */}
        {s.payMethod === 'upi' && s.upiQrUrl && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 'clamp(24px,5vw,64px)',
            padding: 'clamp(20px,3vw,40px) clamp(24px,3.5vw,52px)',
            background: '#f5f3ff', borderTop: '2px solid #ede9fe' }}>

            {/* QR side */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(8px,1.2vw,14px)' }}>
              <div style={{ fontSize: 'clamp(10px,1vw,13px)', fontWeight: 700,
                letterSpacing: '3px', color: '#7c3aed', textTransform: 'uppercase' }}>
                Scan to Pay
              </div>
              <div style={{ background: '#fff', borderRadius: 'clamp(10px,1.5vw,16px)',
                border: '2px solid #ddd6fe', padding: 'clamp(8px,1.2vw,14px)',
                outline: '6px solid #ede9fe' }}>
                <img src={s.upiQrUrl} alt="UPI QR"
                  style={{ width: 'clamp(120px,21vw,260px)', height: 'clamp(120px,21vw,260px)',
                    display: 'block', objectFit: 'contain' }} />
              </div>
              <div style={{ display: 'flex', gap: 'clamp(6px,1vw,14px)', alignItems: 'center' }}>
                {['GPay','PhonePe','Paytm','Any UPI'].map((app, i) => (
                  <span key={app} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1vw,14px)' }}>
                    {i > 0 && <span style={{ color: '#c4b5fd', fontSize: 'clamp(10px,1vw,14px)' }}>·</span>}
                    <span style={{ fontSize: 'clamp(11px,1.1vw,15px)', color: '#7c3aed', fontWeight: 600 }}>{app}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, alignSelf: 'stretch', background: '#ddd6fe', flexShrink: 0 }} />

            {/* Amount side */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(4px,.6vw,8px)' }}>
              <div style={{ fontSize: 'clamp(10px,1vw,13px)', fontWeight: 700,
                letterSpacing: '3px', color: '#7c3aed', textTransform: 'uppercase' }}>
                Total
              </div>
              <div style={{ fontSize: 'clamp(36px,7vw,84px)', fontWeight: 900,
                color: '#4f46e5', letterSpacing: -3, lineHeight: 1 }}>
                {fmt(s.total)}
              </div>
              <div style={{ background: '#ede9fe', borderRadius: 8,
                padding: 'clamp(3px,.5vw,6px) clamp(8px,1.2vw,14px)',
                display: 'inline-block', width: 'fit-content' }}>
                <span style={{ fontSize: 'clamp(10px,1vw,13px)', fontWeight: 600, color: '#7c3aed' }}>
                  UPI payment
                </span>
              </div>
            </div>
          </div>
        )}

        {/* TOTAL row — hidden when UPI QR is showing (amount already shown above) */}
        {!(s.payMethod === 'upi' && s.upiQrUrl) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: 'clamp(14px,2vw,26px) clamp(24px,3.5vw,52px)',
            background: '#f8fafc' }}>
            <span style={{ fontSize: 'clamp(20px,3.5vw,44px)', fontWeight: 900, color: '#64748b' }}>
              TOTAL
            </span>
            <span style={{ fontSize: 'clamp(32px,6.5vw,76px)', fontWeight: 900,
              color: '#4f46e5', letterSpacing: -2 }}>
              {fmt(s.total)}
            </span>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ── Shared header ─────────────────────────────────────────────────────────────
function Header({ connected }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: 'clamp(12px,1.8vw,22px) clamp(24px,3.5vw,52px)',
      background: '#ffffff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 'clamp(24px,3vw,38px)' }}>📚</span>
        <div>
          <div style={{ fontSize: 'clamp(16px,2.2vw,28px)', fontWeight: 800, color: '#0f172a' }}>
            Tapas Reading Cafe
          </div>
          <div style={{ fontSize: 'clamp(9px,.9vw,11px)', color: '#94a3b8',
            letterSpacing: '3px', textTransform: 'uppercase', marginTop: 1 }}>
            Point of Sale
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600,
        fontSize: 'clamp(11px,1vw,14px)', color: connected ? '#16a34a' : '#d97706' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%',
          background: 'currentColor', display: 'inline-block' }} />
        {connected ? 'Live' : 'Connecting…'}
      </div>
    </div>
  );
}

const shell = {
  position: 'fixed', inset: 0, background: '#e2e8f0', color: '#0f172a',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden',
};

const col = {
  width: '100%', maxWidth: 1000,
  display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden',
  background: '#ffffff',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 4px 32px rgba(0,0,0,0.08)',
};
